import { requireService } from '@profgeosoft/di-ez';
import { BackLight } from '@repo/go-icons';
import { Button, Loader, Scrollbar } from '@repo/go-ui';
import { clsx } from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { wrap } from 'src/packages/mobx-di/wrap';
import { hasValue } from 'src/packages/utils/has-value';

import styles from './models-list.module.scss';

export const MIN_SIZE = { width: 220 };

export const ModelsList = wrap(function ModelsList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { modelId } = useParams();
  const directoriesList = requireService('directoriesListService');
  const directoryPage = requireService('directoryPage');
  const ref = useRef<HTMLDivElement>(null);

  const { directoryModelService, isEditing } = directoryPage;
  const view = directoryModelService?.view;

  const headingText = view ? t(`labels:${view.mainDisplayAttr}.label`) : '';

  const [maxSize, setMaxSize] = useState({ width: document.body.clientWidth });
  const [isResizing, setIsResizing] = useState(false);
  const [size, setSize] = useState({ width: MIN_SIZE.width });

  const [startSize, setStartSize] = useState<{ width: number } | null>(null);
  const [startPosition, setStartPosition] = useState<{ x: number } | null>(null);

  const onBack = () => {
    if (!directoriesList.currentDirectory) {
      navigate('/');
      return;
    }

    navigate(`/${directoriesList.currentDirectory}`);
  };

  const onModelClick = (modelId: number) => {
    if (modelId === directoryModelService?.currentModelId) {
      return;
    }

    const isNewModelSet = directoryModelService?.setCurrentModel(modelId);

    if (isNewModelSet) {
      navigate(modelId.toString());
    }
  };

  const handleResize = useCallback(
    (event: PointerEvent & { layerX: number }) => {
      if (!startSize) {
        return;
      }

      const deltaX = event.clientX - (startPosition?.x ?? 0);

      const newWidth = Math.max(Math.min(startSize.width + deltaX, maxSize.width), MIN_SIZE.width);
      setSize(() => ({ width: newWidth }));
    },
    [startPosition, startSize, maxSize],
  );

  useEffect(() => {
    if (isResizing) {
      const clb = (e: PointerEvent) => handleResize(e as PointerEvent & { layerX: number });

      document.addEventListener('pointermove', clb);

      return () => {
        document.removeEventListener('pointermove', clb);
      };
    }

    return void 0;
  }, [handleResize, isResizing]);

  useEffect(() => {
    const abortController = new AbortController();

    document.addEventListener('pointerup', () => setIsResizing(false), { signal: abortController.signal });

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    const parentNode = ref.current?.parentElement;

    if (!parentNode) {
      return;
    }

    const resizeObserver = new ResizeObserver((elements) => {
      const elem = elements[0];
      setMaxSize({ width: elem.contentRect.width / 2 });
    });

    resizeObserver.observe(parentNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (maxSize.width < size.width && !isResizing) {
      setSize({ width: maxSize.width });
    }
  }, [maxSize, size.width, isResizing]);

  useEffect(() => {
    return directoryModelService?.init();
  }, [directoryModelService]);

  useEffect(() => {
    if (!modelId && directoryModelService && directoryModelService.modelsList.length) {
      const defaultModelId = directoryModelService.modelsList[0].value;
      const isNewModelSet = directoryModelService.setCurrentModel(defaultModelId);

      if (isNewModelSet) {
        navigate(defaultModelId.toString());
      }
      return;
    }

    if (directoryModelService && directoryModelService.modelsList.length) {
      const modelIdAsNumber = Number(modelId);
      const isExisting = !!directoryModelService.modelsList.find((model) => model.value === modelIdAsNumber);

      if (isExisting) {
        const isNewModelSet = directoryModelService.setCurrentModel(modelIdAsNumber);

        if (isNewModelSet) {
          navigate(modelIdAsNumber.toString());
        }
      } else {
        const isNewModelSet = directoryModelService.setCurrentModel(null);

        if (isNewModelSet) {
          navigate('./');
        }
      }
    }
  }, [modelId, directoryModelService, directoryModelService?.modelsList, navigate]);
  return (
    <>
      <div
        className={styles.wrapper}
        ref={ref}
        style={{
          width: size.width,
          minWidth: size.width,
          maxWidth: maxSize.width,
        }}
      >
        <div
          className={styles.handler}
          onPointerDown={(event) => {
            setStartPosition({ x: event.clientX });
            setStartSize({ width: size.width });
            setIsResizing(true);
          }}
          onPointerUp={() => setIsResizing(false)}
        />
        {!directoryModelService || directoryModelService.isModelsLoading ? (
          <Loader />
        ) : (
          <>
            <h4 className={styles.heading}>{headingText}</h4>
            <div className={styles.listWrapper}>
              <Scrollbar>
                <ul className={styles.list}>
                  {directoryModelService.modelsList.map(({ label, value }) => {
                    const isActive = value === directoryModelService.currentModelId;
                    const errorCount = directoryModelService.errorsCountByModelId[value];

                    return (
                      <button
                        key={value}
                        className={clsx(styles.listButton, isActive && styles.listButton_active)}
                        // disabled={isEditing}
                        onClick={() => onModelClick(value)}
                      >
                        <p className={styles.listButton_text} title={label}>
                          {label}
                        </p>
                        {hasValue(errorCount) && errorCount > 0 && <div className={styles.errorInfo}>{errorCount}</div>}
                      </button>
                    );
                  })}
                </ul>
              </Scrollbar>
            </div>
          </>
        )}
        <Button className={styles.backButton} icon={<BackLight />} disabled={isEditing} onClick={onBack}>
          {t('common:back')}
        </Button>
      </div>
      <div className={styles.tableWrapper}>
        <Outlet />
      </div>
      {isResizing && (
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 1,
            cursor: 'col-resize',
            userSelect: 'none',
          }}
        />
      )}
    </>
  );
});
